import { useCallback, useRef, useState } from 'react';
import { useSettings } from '@wu/widget-core';
import { DEFAULT_AUTO_INTERVAL } from './use-interval';

interface StorageCounterData {
  lastValue?: number;
  lastInterval?: number;
}

interface CounterState {
  value: number;
  interval?: number;
}

export function usePersistentNumberCounter() {
  const { timerStartNumber, widgetId, editMode } = useSettings();
  const initialValue = useRef(timerStartNumber);
  const storageKey = `number-counter-${widgetId}`;

  const saveCounterToStorage = useCallback(
    ({ value, interval }: CounterState) => {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ lastValue: value, lastInterval: interval })
      );
    },
    [storageKey]
  );

  const loadCounterFromStorage = useCallback((): CounterState => {
    const raw = localStorage.getItem(storageKey);
    const defaultCounterState: CounterState = {
      value: timerStartNumber,
      interval: DEFAULT_AUTO_INTERVAL
    };
    if (!raw) {
      return defaultCounterState;
    }

    try {
      const {
        lastValue = timerStartNumber,
        lastInterval = DEFAULT_AUTO_INTERVAL
      }: StorageCounterData = JSON.parse(raw);

      return {
        value: lastValue,
        interval: Math.max(DEFAULT_AUTO_INTERVAL, lastInterval)
      };
    } catch {
      return defaultCounterState;
    }
  }, [storageKey, timerStartNumber]);

  const [counterState, setCounterState] = useState<CounterState>(() => {
    const { value, interval } = loadCounterFromStorage();
    initialValue.current = value;

    return {
      value,
      interval
    };
  });

  const persistCounterState = useCallback(
    (newState: CounterState | ((oldState: CounterState) => CounterState)) => {
      setCounterState((previousState) => {
        const nextState =
          typeof newState === 'function' ? newState(previousState) : newState;

        if (!editMode) {
          saveCounterToStorage(nextState);
        }

        return nextState;
      });
    },
    [saveCounterToStorage, editMode]
  );

  return {
    ...counterState,
    persistCounterState,
    initialValue: initialValue.current
  };
}
